import React, { useState, useMemo, useCallback } from "react";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import AuthPageLayout from "../../../components/layout/AuthPageLayout";
import { Form } from "../../../components/Form";
import FormTextInput from "../../../components/Form/FormTextInput";
import FormFieldErrors from "../../../components/Form/FormFieldErrors";
import {
  FormDataType,
  FormFieldValue,
} from "../../../components/Form/FormContext";
import { twoFactorBackupAuth } from "../../../store/actions/authActions";
import { ROUTES, STORAGE_KEYS } from "../../../constants";
import { useApiErrorHandling } from "../../../hooks";
import { ValidationResult } from "../../../hooks/useFormValidation";
import {
  BackupCodeContainer,
  BackupCodeTitle,
  BackupCodeSubtitle,
  BackupCodeActions,
} from "./BackupCodePage.styled";

interface BackupCodeFormProps {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] };
  onGoBack: () => void;
  isSubmitting: boolean;
}

const BackupCodeFormFields: React.FC<BackupCodeFormProps> = ({
  apiErrors,
  fieldErrors,
  onGoBack,
  isSubmitting,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <FormTextInput
        name="backupCode"
        label={t("backupCode.backupCode")}
        placeholder={t("backupCode.backupCodePlaceholder")}
        required
      />

      <FormFieldErrors apiErrors={apiErrors} fieldErrors={fieldErrors} />

      <BackupCodeActions>
        <Button
          type="button"
          variant="outlined"
          onClick={onGoBack}
          disabled={isSubmitting}
        >
          {t("backupCode.goBack")}
        </Button>

        <Button type="submit" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? t("backupCode.verifying") : t("backupCode.verify")}
        </Button>
      </BackupCodeActions>
    </>
  );
};

const BackupCodePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiErrors, fieldErrors, clearErrors, handleApiError } =
    useApiErrorHandling({
      defaultErrorMessage: "Invalid backup code. Please try again.",
    });

  const validationRules = useMemo(
    () => ({
      backupCode: {
        required: true,
        custom: (value: FormFieldValue) => {
          const strValue = String(value || "");
          if (!strValue) {
            return t("backupCode.validation.required");
          }
          if (strValue.length < 8) {
            return t("backupCode.validation.minLength");
          }
          return null;
        },
      },
    }),
    [t]
  );

  const handleSubmit = useCallback(
    async (formData: FormDataType, validationResult: ValidationResult) => {
      if (!validationResult?.isValid) {
        return;
      }

      // Get email from sessionStorage
      const email = sessionStorage.getItem(STORAGE_KEYS.EMAIL);
      if (!email) {
        handleApiError({
          message: "Email not found. Please try logging in again.",
        });
        return;
      }

      clearErrors();
      setIsSubmitting(true);

      try {
        // Verify backup code
        await dispatch(
          twoFactorBackupAuth({
            email,
            backupCode: String(formData.backupCode),
          })
        ).unwrap();

        // Navigate to dashboard after successful backup code verification
        navigate(ROUTES.DASHBOARD);
      } catch (error: unknown) {
        console.error("Backup code verification failed:", error);
        handleApiError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, navigate, clearErrors, handleApiError]
  );

  const handleGoBack = useCallback(() => {
    navigate(ROUTES.VERIFICATION);
  }, [navigate]);

  return (
    <AuthPageLayout>
      <BackupCodeContainer>
        <BackupCodeTitle>{t("backupCode.title")}</BackupCodeTitle>

        <BackupCodeSubtitle>{t("backupCode.subtitle")}</BackupCodeSubtitle>

        <Form
          validationRules={validationRules}
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            width: "100%",
          }}
        >
          <BackupCodeFormFields
            apiErrors={apiErrors}
            fieldErrors={fieldErrors}
            onGoBack={handleGoBack}
            isSubmitting={isSubmitting}
          />
        </Form>
      </BackupCodeContainer>
    </AuthPageLayout>
  );
};

export default BackupCodePage;
